import { FormikProvider, useFormik } from 'formik'

import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { selectFederalEstimateByTaxQuarter } from '@/features/taxes/quarterly/userTaxEstimates.selector'
import {
  CALCULATE_SAFE_HARBOR_STEP,
  useCalculateSafeHarborSteps,
} from './helpers'
import { selectIsCurrentUserScorp } from '@/selectors/user.selectors'
import {
  SafeHarborType,
  UPDATE_USER_TAX_ESTIMATES_KEY,
  updateUserTaxEstimates,
} from '@/features/taxes/quarterly/userTaxEstimates.slice'
import { Alert, Text, Image, Form, Card, CardColors } from '@/ui'
import { getFetchError } from '@/reducers/fetch'
import {
  TaxProfileAccordions,
  useTaxProfileAccordionCopy,
} from '@/features/taxes/taxes-profile/form-flow/TaxProfileAccordions'
import { CalculateSafeHarborFooter } from './CalculateSafeHarborFooter'
import { centsToDollars, dollarsToCents } from '@/utils/currencyHelpers'
import { TypographyStyles } from '@/styles'

import { QuarterlyTaxEstimateDetail } from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.slice'

// TODO: Migrate to V2
import {
  getFieldName,
  makeNumberSchema,
  FormikRadioToggleButton,
  FormikLabelError,
  makeReqBoolSchema,
} from '@/componentsV1/BaseComponents'

export const QuarterlyTaxRecommendations = ({
  details,
}: {
  details?: QuarterlyTaxEstimateDetail | null
}) => {
  const dispatch = useAppDispatch()
  const isScorp = useReselector(selectIsCurrentUserScorp)

  const { goToPreviousStep, goToNextStep, hasPriorQuarterStep } =
    useCalculateSafeHarborSteps(
      CALCULATE_SAFE_HARBOR_STEP.quarterlyTaxRecommendations,
      details?.taxQuarter
    )

  const federalTaxEstimate = useReselector(
    selectFederalEstimateByTaxQuarter,
    `${details?.taxYear}-${details?.taxQuarter}`
  )

  const error = useReselector(getFetchError, UPDATE_USER_TAX_ESTIMATES_KEY)

  const formik = useFormik({
    enableReinitialize: true,
    validateOnMount: true,
    initialValues: {
      didReceiveRecommendations: federalTaxEstimate?.safeHarborType
        ? federalTaxEstimate?.safeHarborType === SafeHarborType.ES
        : null,
      estimatedTaxAmount:
        federalTaxEstimate?.safeHarborType === SafeHarborType.ES &&
        federalTaxEstimate?.estimateInCents
          ? centsToDollars(federalTaxEstimate.estimateInCents)
          : null,
    },
    onSubmit: async ({ didReceiveRecommendations, estimatedTaxAmount }) => {
      if (!didReceiveRecommendations) {
        goToNextStep()
        return
      }

      if (!federalTaxEstimate) {
        return
      }

      const res = await dispatch(
        updateUserTaxEstimates(federalTaxEstimate?.id, {
          estimateInCents: dollarsToCents(estimatedTaxAmount),
          safeHarborType: SafeHarborType.ES,
        })
      )

      if (res) {
        goToNextStep(
          hasPriorQuarterStep
            ? CALCULATE_SAFE_HARBOR_STEP.priorQuarterPayments
            : CALCULATE_SAFE_HARBOR_STEP.finish
        )
      }
    },
  })

  const { values, isSubmitting, isValid, submitForm } = formik

  const previousYear = Number(details?.taxYear) - 1

  const faqCopy = useTaxProfileAccordionCopy()

  return (
    <FormikProvider value={formik}>
      <div className="max-w-2xl mx-auto space-y-10 mt-10">
        <div className="mx-auto max-w-4xl">
          <Image
            src="https://heard-images.s3.amazonaws.com/assets/1040_es.png"
            className="w-full"
            alt="1040-ES"
          />
        </div>

        <Text variant={TypographyStyles.DISPLAY_SM} className="text-center">
          Did you receive quarterly tax recommendations in {previousYear}?
        </Text>

        <Text variant={TypographyStyles.BODY_LG}>
          Some tax services will provide you with a quarterly tax estimate for
          the coming year with your 1040 tax return. Look for a form called{' '}
          <b>1040-ES Estimated Tax</b>, or any instructions attached to your
          return for recommended payments. Look for the 1040-ES payment voucher
          or recommended amount for the current quarter. If there is an estimate
          provided there, you can use this number for your quarterly tax
          payment.
        </Text>

        <FormikLabelError
          name={getFieldName<typeof values>('didReceiveRecommendations')}
          schema={makeReqBoolSchema()}
        />

        <div className="flex justify-center gap-4">
          <FormikRadioToggleButton
            fullWidth
            name={getFieldName<typeof values>('didReceiveRecommendations')}
            value
          >
            Yes
          </FormikRadioToggleButton>
          <FormikRadioToggleButton
            fullWidth
            name={getFieldName<typeof values>('didReceiveRecommendations')}
            value={false}
          >
            No
          </FormikRadioToggleButton>
        </div>

        {error && <Alert variant="error" description={error.message} />}

        {values.didReceiveRecommendations && (
          <Form.Input
            name={getFieldName<typeof values>('estimatedTaxAmount')}
            componentType="currency"
            schema={makeNumberSchema({
              allowedDecimals: 2,
              greaterThanZero: true,
            })}
            label={`Q${details?.taxQuarter} Estimated Tax`}
            description="Enter the suggested amount from your 1040-ES."
            placeholder="$"
          />
        )}

        {values.didReceiveRecommendations === false && (
          <Card color={CardColors.LEVEL_1}>
            <div className="flex items-center gap-4">
              <Image
                src="https://heard-images.s3.amazonaws.com/assets/ledger.svg"
                className="w-20 h-20"
                alt="Ledger"
              />
              <Text>
                No worries! Proceed to the next screen, and we’ll guide you
                through getting an estimate yourself. Keep that 1040 tax return
                handy...
              </Text>
            </div>
          </Card>
        )}

        <TaxProfileAccordions
          faqs={[
            faqCopy.whoPaysQtes,
            faqCopy.no1040TaxReturn,
            ...(isScorp ? [faqCopy.haveToPayQTEsScorp] : []),
          ]}
        />

        <CalculateSafeHarborFooter
          submitOrContinue={submitForm}
          goToPreviousStep={goToPreviousStep}
          continueDisabled={!isValid || isSubmitting}
          loading={isSubmitting}
          nextText={hasPriorQuarterStep ? undefined : 'Save'}
        />
      </div>
    </FormikProvider>
  )
}
